import React, { useEffect, useState } from "react";
import {
  Button,
  chakra,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput, FormSelect } from "../../../components/Forms";
import { useGetOrganizationStructure } from "../../../hooks/query/questionnaire/organization-questionnaire";
import { useGetOrganizationUsers } from "../../../hooks/query/organization-administration";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useGetDimensions } from "../../../hooks/query/dimensions";
import { dimensionsKeys, filterActiveDimensionItems, showErrorToast } from "../../../utils";
import { sortUsersByEmail } from "../../../utils/userSort";

const ALL_VALUE = "ALL";

function mapDataToFormData(data) {
  const mapped = {
    id: data?.id,
    name: data?.name ?? "",
    dimensionOne: data?.dimensionItems?.dimensionOneItem ?? ALL_VALUE,
    dimensionTwo: data?.dimensionItems?.dimensionTwoItem ?? ALL_VALUE,
    dimensionThree: data?.dimensionItems?.dimensionThreeItem ?? ALL_VALUE,
    dimensionFour: data?.dimensionItems?.dimensionFourItem ?? ALL_VALUE,
    dimensionFive: data?.dimensionItems?.dimensionFiveItem ?? ALL_VALUE,
    assignee: data?.assigneeId ?? "",
  };

  return mapped;
}

export function TemplateModal({ modalData, isOpen, onClose, onSubmit }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "taskAssigneesTemplate",
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({});

  const formValues = watch();

  // Function to get dimensions query params based on selected values
  const getDimensionsQueryParams = () => {
    const queryParams = {};
    dimensionsKeys.forEach((key) => {
      if (typeof formValues[key] !== "undefined" && formValues[key] !== ALL_VALUE) {
        queryParams[`${key}Item`] = formValues[key];
      }
    });
    return queryParams;
  };

  // Get dimensions data from API
  const { data: dimensionsData } = useGetDimensions(getDimensionsQueryParams(), {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  // Fetch organization structure
  const { data: organizationStructure, isLoading: isOrgStructureLoading } = useGetOrganizationStructure({
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      handleModalClose();
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  // Fetch organization users
  const { data: users, isLoading: isUsersLoading } = useGetOrganizationUsers({
    onError: (err) => {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      handleModalClose();
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  // Show spinner while loading
  useEffect(() => {
    if (isOrgStructureLoading || isUsersLoading) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isOrgStructureLoading, isUsersLoading]);

  useEffect(() => {
    if (isOpen && !isUsersLoading) {
      if (modalData) {
        // Editing existing template
        reset(mapDataToFormData(modalData));
      } else {
        // Creating new template
        reset(mapDataToFormData());
      }
    }
  }, [isOpen, modalData, reset, isUsersLoading]);

  const prepareSubmitData = (data) => {
    // const selectedUser = users ? users.find((user) => user.id === data.assignee) : null;

    const dataToSave = {
      name: data.name,
      dimensionItems: {
        dimensionOneItem: data.dimensionOne !== ALL_VALUE ? data.dimensionOne : null,
        dimensionTwoItem: data.dimensionTwo !== ALL_VALUE ? data.dimensionTwo : null,
        dimensionThreeItem: data.dimensionThree !== ALL_VALUE ? data.dimensionThree : null,
        dimensionFourItem: data.dimensionFour !== ALL_VALUE ? data.dimensionFour : null,
        dimensionFiveItem: data.dimensionFive !== ALL_VALUE ? data.dimensionFive : null,
      },
      assigneeId: data.assignee,
    };

    onSubmit(modalData?.id, dataToSave);
  };

  const handleModalClose = () => {
    onClose();
  };

  const getModalHeaderText = () => {
    return modalData ? t("editTemplateHeader") : t("createTemplateHeader");
  };

  const renderDimensionOptions = (dimKey) => {
    let dimItems = [{ id: ALL_VALUE, name: "All" }];

    // Use dimensionsData from API if available, otherwise fallback to organization structure
    if (
      dimensionsData &&
      dimensionsData[dimKey] &&
      dimensionsData[dimKey].items &&
      dimensionsData[dimKey].items.length > 0
    ) {
      dimItems.push(...dimensionsData[dimKey].items);
    }
    // else if (organizationStructure &&
    //     organizationStructure.dimensions &&
    //     organizationStructure.dimensions[dimKey] &&
    //     organizationStructure.dimensions[dimKey].items &&
    //     organizationStructure.dimensions[dimKey].items.length > 0) {
    //   dimItems.push(...organizationStructure.dimensions[dimKey].items);
    // }

    const currentValue = formValues[dimKey];
    const allOption = dimItems[0];
    const filtered = filterActiveDimensionItems(dimItems.slice(1), currentValue);
    return [allOption, ...filtered].map((item) => (
      <option key={`${dimKey}-${item?.id || "unknown"}`} value={item?.id || ""}>
        {item?.name || ""}
      </option>
    ));
  };

  return !isOpen ? null : (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
          <ModalHeader>{getModalHeaderText()}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Flex direction="column" gap="2">
              <FormInput
                label={t("templateName")}
                type="text"
                errors={errors}
                {...register("name", {
                  required: globalTranslation("entityRequired", { entityName: t("templateName") }),
                })}
              />

              {organizationStructure &&
                organizationStructure.dimensions &&
                Object.keys(organizationStructure.dimensions)
                  .filter((dimKey) => dimKey.startsWith("dimension"))
                  .map((dimKey) => {
                    if (!organizationStructure.dimensions[dimKey]) return null;
                    return (
                      <FormSelect
                        {...register(`${dimKey}`)}
                        key={`${dimKey}-select`}
                        label={`${organizationStructure.dimensions[dimKey]?.name || dimKey}`}
                        errors={errors}
                      >
                        {renderDimensionOptions(dimKey)}
                      </FormSelect>
                    );
                  })}

              <FormSelect
                label={`${t("assignee")}`}
                placeholder=" "
                errors={errors}
                {...register("assignee", {
                  required: globalTranslation("entityRequired", { entityName: t("assignee") }),
                })}
              >
                {users
                  ? sortUsersByEmail(users).map((assignee) => (
                      <option key={assignee.id} value={assignee.id}>
                        {assignee.email}
                      </option>
                    ))
                  : []}
              </FormSelect>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              {t("cancel")}
            </Button>
            <Button colorScheme="customScheme" type="submit">
              {t("save")}
            </Button>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal>
  );
}
